import { useToast } from '@ui/toasts/useToastManager'
import {
  DiscountType,
  DiscountTypes,
  ErpOrderDiscountedPrice,
  NumberUtils,
} from '@ca/common'
import {
  DiscountConfig,
  OrderScenario,
  RegionConfig,
  RemoteConfig,
} from '@ca/common/types'
import {
  CouponDiscountOption,
  CreditDiscountOption,
  DiscountOption,
  GiftCardDiscountOption,
  PromotionDiscountOption,
  ReferralDiscountOption,
} from '@order/orderTypes'
import { trpc } from 'api'
import Analytics from 'hooks/analytics/Analytics'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import groupBy from 'lodash/groupBy'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import nanoid from '@ui/utils/nanoid'
import { useOrderState } from './hooks/useOrderState'
import useOrderTotal, { getInstallmentValue } from './hooks/useOrderTotal'

import useCartState, { useSavedOrder } from './hooks/useCartState'
import { useCurrentScenario, useOrderScenarios } from './hooks/useScenario'
import getDiscountedTotals from './utils/getDiscountedTotals'
import resolveDiscountPrices from './utils/resolveDiscountPrices'

export type DiscountState = {
  discounts: DiscountOption[]
}

export type DiscountStateActions = {
  resetDiscounts: () => void
  addDiscount: (discount: DiscountOption) => void
  removeDiscount: (discountId: string) => void
}

export const initialDiscountState: DiscountState = {
  discounts: [],
}

export const DiscountConfigDefaults: Record<DiscountType, DiscountConfig> = {
  credit: {
    other: ['gift-card', 'promotion'],
    max: 1,
    scenarios: ['base.normal'],
  },
  coupon: {
    other: ['referral', 'gift-card', 'promotion'],
    max: 1,
    scenarios: ['base.normal', 'base.tgbo'],
  },
  referral: {
    other: ['coupon', 'gift-card', 'promotion'],
    max: 1,
    scenarios: ['base.normal'],
  },
  'gift-card': {
    other: ['coupon', 'referral', 'credit', 'promotion'],
    max: -1,
    scenarios: ['base.normal', 'base.tgbo'],
  },
  promotion: {
    // TODO: check this
    other: ['credit', 'coupon', 'referral', 'gift-card'],
    max: -1,
    scenarios: ['base.normal', 'base.tgbo'],
  },
} as const

type DiscountCheckParams = {
  scenario?: OrderScenario
  type: DiscountType
  items: DiscountOption[]
  config: RemoteConfig | null
}

// We can use this to migrate to better config structure in the future
const resolveConfig = (
  type: DiscountType,
  config: RegionConfig | null | undefined
) => {
  return config?.[type] || DiscountConfigDefaults[type]
}

const isVisible = ({ type, scenario, config }: DiscountCheckParams) => {
  const discountConfig = resolveConfig(type, config?.region)

  // Fallback to the default scenarios if not set for the type
  const scenarios =
    discountConfig.scenarios || DiscountConfigDefaults[type].scenarios

  if (scenario && scenarios?.length && !scenarios.includes(scenario)) {
    return false
  }
  return true
}

const canAdd = (params: DiscountCheckParams) => {
  const { type, items, config } = params
  const discountConfig = resolveConfig(type, config?.region)

  const count = items.filter((item) => item.mode === type).length
  const max = discountConfig.max === -1 ? Infinity : discountConfig.max
  if (count >= max) {
    return false
  }

  if (items.length === 0) {
    return true
  }
  return items.every(
    (item) => item.mode === type || discountConfig.other.includes(item.mode)
  )
}

const getDiscountState = (params: DiscountCheckParams) => {
  const visible = isVisible(params)
  if (!visible) {
    return {
      enabled: false,
      visible: false,
    }
  }

  return {
    enabled: canAdd(params),
    visible: true,
  }
}

export const isDiscountTypeEnabled = (
  type: DiscountType,
  scenario: OrderScenario,
  config: RemoteConfig | null
) => {
  const params = { type, scenario, config, items: [] }
  return isVisible(params) && canAdd(params)
}

export const isDiscountsEnabledForScenario = (
  scenario: OrderScenario,
  config: RemoteConfig | null
) => DiscountTypes.some((type) => isDiscountTypeEnabled(type, scenario, config))

export type SharedDiscountOptions = {
  onSuccess?: () => void
  onError?: () => void
}

export type DiscountStateOptions = {
  state: DiscountOption[]
  scenario: OrderScenario
  addDiscount: (discount: DiscountOption) => void
  removeDiscount: (discountId: string) => void
  applyDiscountedOrderPrices?: (orders: ErpOrderDiscountedPrice[]) => void
  clearFinalPrices?: (orderIds: number[]) => void
}

/**
 * This should not be used directly
 */
const useDiscountsInternal = ({
  onSuccess,
  onError,
  state = [],
  addDiscount,
  scenario,
  removeDiscount,
  applyDiscountedOrderPrices,
  clearFinalPrices,
}: SharedDiscountOptions & DiscountStateOptions) => {
  const config = useRemoteConfig()
  const toast = useToast()
  const { t } = useTranslation(['toasts'])

  const applyMutation = trpc.order.discounts.applyDiscount.useMutation({
    onSuccess: (res, req) => {
      addDiscount({
        id: nanoid(),
        ...res,
      })
      if (applyDiscountedOrderPrices && res.orders?.length) {
        applyDiscountedOrderPrices(res.orders)
      }

      if (res.mode === 'credit') {
        Analytics.logEvent('discount_applied', {
          type: res.mode,
          amount: `${res.discount}`,
          description: res.description || '',
          orders: req.input.orders.map((o) => o.id).join(','),
          points_ids: res.points_ids.join(','),
        })
      }
      if (res.mode === 'coupon' || res.mode === 'referral') {
        Analytics.logEvent('discount_applied', {
          type: res.mode,
          amount: `${res.discount}`,
          description: res.description || '',
          orders: req.input.orders.map((o) => o.id).join(','),
          voucher_ids: res.voucher_ids.join(','),
        })
      }

      onSuccess?.()
    },
    onError: (err) => {
      // TODO: Better error handling
      toast({
        title: t('toasts:error.default.title'),
        text: err.message ?? t('toasts:error.default.desc'),
        variant: 'error',
      })
      onError?.()
    },
  })

  const removeMutation = trpc.order.discounts.removeDiscount.useMutation({
    onSuccess: (res, req) => {
      const { prices, clearedIds } = resolveDiscountPrices(
        res,
        req.input.orders.map(({ id }) => id)
      )
      if (prices.length) {
        applyDiscountedOrderPrices?.(prices)
        return
      }
      clearFinalPrices?.(clearedIds)
    },
    onError: (err) => {
      // TODO: Better error handling
      toast({
        title: t('toasts:error.default.title'),
        text: err.message ?? t('toasts:error.default.desc'),
        variant: 'error',
      })
    },
  })

  const remove = async (id: string) => {
    const discount = state.find((item) => item.id === id)
    if (!discount) {
      return
    }
    await removeMutation.mutateAsync(discount)
    removeDiscount(id)
  }

  const derived = useMemo(() => {
    // TS doesn't know that the groupBy function returns a record with the correct types
    const grouped = groupBy(state, 'mode') as {
      credit?: CreditDiscountOption[]
      coupon?: CouponDiscountOption[]
      referral?: ReferralDiscountOption[]
      'gift-card'?: GiftCardDiscountOption[]
      promotion?: PromotionDiscountOption[]
    }

    return {
      credit: {
        ...getDiscountState({ type: 'credit', items: state, config, scenario }),
        items: grouped.credit ?? [],
      },
      coupon: {
        ...getDiscountState({ type: 'coupon', items: state, config, scenario }),
        items: grouped.coupon ?? [],
      },
      referral: {
        ...getDiscountState({
          type: 'referral',
          items: state,
          config,
          scenario,
        }),
        items: grouped.referral ?? [],
      },
      'gift-card': {
        ...getDiscountState({
          type: 'gift-card',
          items: state,
          config,
          scenario,
        }),
        items: grouped['gift-card'] ?? [],
      },
      promotion: {
        ...getDiscountState({
          type: 'promotion',
          items: state,
          config,
          scenario,
        }),
        items: grouped.promotion ?? [],
      },
    } as const
  }, [state, config, scenario])
  // console.log('derived', derived)
  return {
    ...derived,
    items: state,
    add: applyMutation.mutateAsync,
    remove,
    isPending: applyMutation.isPending || removeMutation.isPending,
  }
}

export const useTotalDiscountAmount = ({
  allowed,
  orderTotal,
  discounts,
}: {
  discounts: DiscountOption[]
  orderTotal: ReturnType<typeof useOrderTotal>
  allowed?: Record<DiscountType, boolean> | undefined
}) => {
  const custom = useMemo(() => {
    const sum = discounts.reduce((acc, item) => {
      if (allowed && !allowed[item.mode]) {
        return acc
      }

      return acc + item.discount
    }, 0)
    return NumberUtils.round.decimal(sum)
  }, [discounts, allowed])

  const { discounted, base } = getDiscountedTotals(orderTotal, custom)

  const discountedMonthly = getInstallmentValue(
    discounted,
    orderTotal.installmentCount ?? 0
  )
  const discountedDisplay = orderTotal.showMonthly
    ? discountedMonthly
    : discounted

  return {
    hasDiscounts: discounts.length > 0,
    custom,
    discounted,
    discountedMonthly,
    discountedDisplay,
    auto: orderTotal?.remote
      ? Math.min(0, discounted - orderTotal.local - custom)
      : undefined,
    total: orderTotal.remote ?? orderTotal.local,
    base,
    // auto: total?.remote ? Math.min(0, total.remote - total.local) : undefined,
  }
}

export const useCurrentOrderDiscountsOptions = () => {
  const addDiscount = useOrderState((s) => s.addDiscount)
  const removeDiscount = useOrderState((s) => s.removeDiscount)
  const state = useOrderState((s) => s.discounts)
  const scenario = useCurrentScenario()
  const applyDiscountedOrderPrices = useOrderState(
    (s) => s.applyDiscountedOrderPrices
  )
  const clearFinalPrices = useOrderState((s) => s.clearFinalPrices)

  return {
    state,
    scenario,
    addDiscount,
    removeDiscount,
    applyDiscountedOrderPrices,
    clearFinalPrices,
  } satisfies DiscountStateOptions
}

export const useSavedOrderDiscountsOptions = (orderId: string) => {
  const addDiscount = useCartState((s) => s.discounts.add)
  const removeDiscount = useCartState((s) => s.discounts.remove)
  const applyCartFinalPrices = useCartState((s) => s.applyDiscountedOrderPrices)
  const clearCartFinalPrices = useCartState((s) => s.clearFinalPrices)
  const scenarios = useOrderScenarios(orderId)

  const order = useSavedOrder(orderId)

  const add = useCallback(
    (discount: DiscountOption) => {
      addDiscount(orderId, discount)
    },
    [addDiscount, orderId]
  )

  const remove = useCallback(
    (discountId: string) => {
      removeDiscount(orderId, discountId)
    },
    [removeDiscount, orderId]
  )

  const applyDiscountedOrderPrices = useCallback(
    (orders: ErpOrderDiscountedPrice[]) => {
      applyCartFinalPrices(orderId, orders)
    },
    [applyCartFinalPrices, orderId]
  )

  const clearFinalPrices = useCallback(
    (orderIds: number[]) => {
      clearCartFinalPrices(orderId, orderIds)
    },
    [clearCartFinalPrices, orderId]
  )

  return {
    state: order?.discounts || [],
    // FIXME: We should fix this to use all scenarios
    scenario: scenarios[0],
    addDiscount: add,
    removeDiscount: remove,
    applyDiscountedOrderPrices,
    clearFinalPrices,
  } satisfies DiscountStateOptions
}

export const useDiscountsEnabled = (scenario: OrderScenario) => {
  const config = useRemoteConfig()
  return useMemo(
    () => isDiscountsEnabledForScenario(scenario, config),
    [scenario, config]
  )
}

export const useDiscountTypeEnabled = (
  type: DiscountType,
  scenario: OrderScenario | undefined,
  fallback = true
) => {
  const config = useRemoteConfig()
  return useMemo(() => {
    if (!scenario) {
      return fallback
    }

    return isDiscountTypeEnabled(type, scenario, config)
  }, [type, scenario, config, fallback])
}

export const useDiscounts = (
  orderId: string | undefined,
  options: SharedDiscountOptions
) => {
  const current = useCurrentOrderDiscountsOptions()
  const saved = useSavedOrderDiscountsOptions(orderId || '')

  const selected = orderId ? saved : current

  return useDiscountsInternal({
    ...options,
    ...selected,
  })
}
